/**
 * Modelo y reglas de la importación de productos a una zona de evento.
 * Funciones puras: reciben filas de texto (CSV, Excel o lo que extrajo la
 * IA de un PDF/captura) y devuelven filas listas para escribir, con sus
 * avisos. Nada de Angular ni de Firestore aquí, para poder probarlo aparte.
 */

export type ImportField = 'name' | 'price' | 'inventory' | 'zone' | 'section' | 'code' | 'supplier' | 'visible' | 'ignore';

export interface ImportFieldDef {
    key: ImportField;
    label: string;
    hint: string;
    required?: boolean;
}

/** Columnas que entiende la importación, en el orden de la plantilla. */
export const IMPORT_FIELDS: ImportFieldDef[] = [
    { key: 'name', label: 'Nombre', hint: 'Nombre del producto tal como se verá en la app', required: true },
    { key: 'price', label: 'Precio', hint: 'Precio de venta: 8, 8.50 o $8.50' },
    { key: 'inventory', label: 'Inventario', hint: 'Existencias disponibles (entero)' },
    { key: 'zone', label: 'Ubicación', hint: 'Zona o barra del evento donde se vende' },
    { key: 'section', label: 'Sección', hint: 'Pestaña de la caja: Botellas, Tragos, Cervezas, Sin alcohol…' },
    { key: 'code', label: 'Código', hint: 'SKU o código interno' },
    { key: 'supplier', label: 'Proveedor', hint: 'Marca o proveedor' },
    { key: 'visible', label: 'Visible', hint: 'Sí / No' },
    { key: 'ignore', label: 'Ignorar', hint: 'No importar esta columna' },
];

/** Encabezados de la plantilla oficial (y de la exportación), en este orden. */
export const TEMPLATE_HEADERS = ['Nombre', 'Código', 'Proveedor', 'Precio', 'Inventario', 'Ubicación', 'Sección', 'Visible'];
export const TEMPLATE_FIELDS: ImportField[] = ['name', 'code', 'supplier', 'price', 'inventory', 'zone', 'section', 'visible'];

const SYNONYMS: { [key: string]: string[] } = {
    name: ['nombre', 'producto', 'name', 'product', 'descripcion', 'description', 'articulo', 'item', 'detalle', 'nombre del producto'],
    price: ['precio', 'price', 'pvp', 'valor', 'precio venta', 'precio de venta', 'precio unitario', 'unit price', 'monto', 'costo'],
    inventory: ['inventario', 'stock', 'existencia', 'existencias', 'cantidad', 'qty', 'quantity', 'inventory', 'disponible', 'disponibles', 'unidades', 'cant'],
    zone: ['ubicacion', 'zona', 'barra', 'location', 'zone', 'punto de venta', 'estacion', 'area', 'lugar', 'bar', 'sector'],
    section: ['seccion', 'section', 'categoria', 'category', 'pestana', 'grupo', 'familia', 'tipo', 'tipo de producto', 'clasificacion'],
    code: ['codigo', 'code', 'sku', 'referencia', 'ref', 'cod', 'system code', 'clave', 'codigo interno', 'id'],
    supplier: ['proveedor', 'supplier', 'marca', 'brand', 'distribuidor', 'vendor', 'proveedor/marca'],
    visible: ['visible', 'activo', 'active', 'mostrar', 'habilitado', 'enabled', 'publicado'],
};

/** Minúsculas, sin tildes ni espacios repetidos: así se comparan nombres y encabezados. */
export function normalizeText(text: any): string {
    return String(text === null || text === undefined ? '' : text)
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[\s_\-\.]+/g, ' ')
        .trim();
}

// ═══════════════════════════════════════════════════════════════════════════
//  ENCABEZADOS Y MAPEO DE COLUMNAS
// ═══════════════════════════════════════════════════════════════════════════

function fieldForHeader(header: string): ImportField {
    const clean = normalizeText(header);
    if (!clean) return 'ignore';
    const keys = Object.keys(SYNONYMS);
    for (const key of keys) {
        if (SYNONYMS[key].indexOf(clean) >= 0) return key as ImportField;
    }
    for (const key of keys) {
        if (SYNONYMS[key].some(s => clean.indexOf(s) === 0 || (s.length >= 4 && clean.indexOf(s) >= 0))) {
            return key as ImportField;
        }
    }
    return 'ignore';
}

/** Adivina qué campo es cada columna por su encabezado; cada campo se usa una sola vez. */
export function guessMapping(headers: string[]): ImportField[] {
    const used: { [key: string]: boolean } = {};
    return headers.map(header => {
        const field = fieldForHeader(header);
        if (field === 'ignore' || used[field]) return 'ignore';
        used[field] = true;
        return field;
    });
}

/** ¿La primera fila son encabezados y no un producto? */
export function looksLikeHeader(row: string[]): boolean {
    if (!row || row.length === 0) return false;
    const recognized = row.filter(cell => fieldForHeader(cell) !== 'ignore').length;
    const numeric = row.filter(cell => cell !== '' && parsePrice(cell) !== null).length;
    return recognized >= 1 && numeric === 0;
}

/** Cuando no hay encabezados: nombre, precio, inventario, ubicación por posición. */
export function positionalMapping(columns: number): ImportField[] {
    const order: ImportField[] = ['name', 'price', 'inventory', 'zone', 'code', 'supplier', 'visible', 'section'];
    const mapping: ImportField[] = [];
    for (let i = 0; i < columns; i++) mapping.push(i < order.length ? order[i] : 'ignore');
    return mapping;
}

// ═══════════════════════════════════════════════════════════════════════════
//  VALORES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * "$8.50", "8,50", "L 1,500", "1.500,00" → número. Con los dos separadores
 * manda el último; con uno solo, tres dígitos detrás se leen como miles
 * (1,500 → 1500) y cualquier otra cosa como decimales (8,5 → 8.5).
 */
export function parsePrice(text: any): number | null {
    if (typeof text === 'number') return isFinite(text) ? text : null;
    let clean = String(text === null || text === undefined ? '' : text).trim();
    if (!clean) return null;
    clean = clean.replace(/[^\d,.\-]/g, '');
    if (!clean || clean === '-' || clean === '.' || clean === ',') return null;

    const lastComma = clean.lastIndexOf(',');
    const lastDot = clean.lastIndexOf('.');
    if (lastComma >= 0 && lastDot >= 0) {
        clean = lastComma > lastDot
            ? clean.replace(/\./g, '').replace(',', '.')
            : clean.replace(/,/g, '');
    } else if (lastComma >= 0 || lastDot >= 0) {
        const sep = lastComma >= 0 ? ',' : '.';
        const parts = clean.split(sep);
        const decimals = parts[parts.length - 1];
        if (parts.length > 2 || decimals.length === 3) {
            clean = parts.join('');
        } else {
            clean = parts.join('.');
        }
    }

    const value = Number(clean);
    return isFinite(value) ? Math.round(value * 100) / 100 : null;
}

/** "12", "12 unidades", "12.0" → 12. Vacío → null. */
export function parseInventory(text: any): number | null {
    if (typeof text === 'number') return isFinite(text) ? Math.round(text) : null;
    const clean = String(text === null || text === undefined ? '' : text).trim();
    if (!clean) return null;
    const value = parsePrice(clean);
    return value === null ? null : Math.round(value);
}

const YES = ['si', 's', 'yes', 'y', 'true', '1', 'x', 'visible', 'activo', 'mostrar', 'habilitado', 'ok', 'verdadero'];
const NO = ['no', 'n', 'false', '0', 'oculto', 'inactivo', 'ocultar', 'deshabilitado', 'falso'];

export function parseVisible(text: any): boolean | null {
    if (typeof text === 'boolean') return text;
    const clean = normalizeText(text);
    if (!clean) return null;
    if (YES.indexOf(clean) >= 0) return true;
    if (NO.indexOf(clean) >= 0) return false;
    return null;
}

// ═══════════════════════════════════════════════════════════════════════════
//  FILAS
// ═══════════════════════════════════════════════════════════════════════════

/** Una fila del archivo ya leída campo por campo, antes de validarla. */
export interface ImportRow {
    /** Número de fila en el archivo (1 = primera fila de datos). */
    line: number;
    name: string;
    price: number | null;
    inventory: number | null;
    zone: string;
    /** Pestaña de la caja (Botellas, Tragos…); texto libre por producto. */
    section: string;
    code: string;
    supplier: string;
    visible: boolean | null;
}

/**
 * Nombre de sección tal como se guarda: sin espacios sobrantes y con la
 * inicial en mayúscula (misma regla que event-products/product-sections,
 * repetida aquí para no importar en círculo).
 */
export function cleanSection(text: any): string {
    const clean = String(text === null || text === undefined ? '' : text).replace(/\s+/g, ' ').trim();
    return clean ? clean.charAt(0).toUpperCase() + clean.slice(1) : '';
}

/** Aplica el mapeo de columnas a las filas crudas del archivo. */
export function rowsFromTable(table: string[][], mapping: ImportField[], hasHeader: boolean): ImportRow[] {
    const data = hasHeader ? table.slice(1) : table;
    return data.map((cells, i) => {
        const row: ImportRow = {
            line: i + 1, name: '', price: null, inventory: null, zone: '', section: '', code: '', supplier: '', visible: null,
        };
        mapping.forEach((field, c) => {
            const value = cells[c] === undefined || cells[c] === null ? '' : String(cells[c]).trim();
            switch (field) {
                case 'name': row.name = value; break;
                case 'price': row.price = parsePrice(value); break;
                case 'inventory': row.inventory = parseInventory(value); break;
                case 'zone': row.zone = value; break;
                case 'section': row.section = cleanSection(value); break;
                case 'code': row.code = value; break;
                case 'supplier': row.supplier = value; break;
                case 'visible': row.visible = parseVisible(value); break;
                default: break;
            }
        });
        return row;
    }).filter(row => row.name || row.price !== null || row.inventory !== null || row.code || row.supplier || row.zone || row.section);
}

/** Lo que devuelve la extracción con IA de un PDF o una captura. */
export interface ExtractedProduct {
    name: string;
    price?: number | null;
    inventory?: number | null;
    zone?: string | null;
    section?: string | null;
    code?: string | null;
    supplier?: string | null;
    visible?: boolean | null;
}

export function rowsFromExtraction(products: ExtractedProduct[]): ImportRow[] {
    return (products || []).map((p, i) => ({
        line: i + 1,
        name: String(p.name || '').trim(),
        price: p.price === null || p.price === undefined ? null : parsePrice(p.price),
        inventory: p.inventory === null || p.inventory === undefined ? null : parseInventory(p.inventory),
        zone: String(p.zone || '').trim(),
        section: cleanSection(p.section),
        code: String(p.code || '').trim(),
        supplier: String(p.supplier || '').trim(),
        visible: p.visible === null || p.visible === undefined ? null : !!p.visible,
    })).filter(row => row.name);
}

// ═══════════════════════════════════════════════════════════════════════════
//  PREPARACIÓN: zona destino, crear o actualizar, avisos
// ═══════════════════════════════════════════════════════════════════════════

export interface ImportZone {
    key: string;
    name: string;
    /**
     * Ruta desde la zona actual para las categorías anidadas
     * ("Precio Regular / Botellas"); las zonas de primer nivel no la traen.
     */
    path?: string;
}

export interface ExistingProduct {
    key: string;
    name: string;
}

export interface ImportIssue {
    /** error: la fila se omite · warning: se importa, pero conviene revisar · info: solo contexto */
    level: 'error' | 'warning' | 'info';
    message: string;
}

export type ImportAction = 'create' | 'update' | 'skip';

/**
 * Llave provisional de una ubicación que todavía no existe: el importador
 * la crea como categoría dentro de la zona actual y la reemplaza por la
 * llave real antes de escribir los productos.
 */
export const NEW_ZONE_PREFIX = 'new:';

export function isNewZoneKey(key: string): boolean {
    return typeof key === 'string' && key.indexOf(NEW_ZONE_PREFIX) === 0;
}

/** Nombre de una ubicación nueva tal como se creará: sin espacios sobrantes. */
export function cleanZoneName(text: any): string {
    return String(text === null || text === undefined ? '' : text).replace(/\s+/g, ' ').trim();
}

/** Separador con el que se escriben y se muestran las rutas de categorías. */
export const ZONE_PATH_SEPARATOR = ' / ';

/**
 * "Precio Regular / Botellas", "Precio Regular > Botellas" o "Precio Regular › Botellas"
 * → ['Precio Regular', 'Botellas']: cada tramo es una categoría dentro de la anterior.
 */
export function splitZonePath(text: any): string[] {
    return String(text === null || text === undefined ? '' : text)
        .split(/\s*[\/>›»\\]\s*/)
        .map(cleanZoneName)
        .filter(part => !!part);
}

export function joinZonePath(parts: string[]): string {
    return (parts || []).map(cleanZoneName).filter(p => !!p).join(ZONE_PATH_SEPARATOR);
}

export interface PreparedRow {
    row: ImportRow;
    action: ImportAction;
    zoneKey: string;
    zoneName: string;
    /** $key del producto existente cuando se actualiza. */
    existingKey: string;
    /** true: la ubicación no existe y se creará como categoría dentro de la zona actual (zoneKey es provisional). */
    newZone: boolean;
    /** Ruta de la categoría desde la zona actual ("Precio Regular / Botellas"); vacía para zonas de primer nivel. */
    zonePath: string;
    name: string;
    price: number;
    inventory: number;
    visible: boolean;
    /**
     * Al actualizar, una celda vacía conserva el valor que ya tiene el
     * producto: así un archivo de solo precios no pisa las existencias que
     * ya descontaron las ventas, ni oculta nada.
     */
    keepPrice: boolean;
    keepInventory: boolean;
    keepVisible: boolean;
    code: string;
    supplier: string;
    /** '' = sin sección (o, al actualizar, se conserva la que tiene). */
    section: string;
    issues: ImportIssue[];
}

export interface PrepareOptions {
    /** Zonas del evento que aceptan productos. */
    zones: ImportZone[];
    /** Zona actual: destino cuando la fila no trae ubicación. */
    defaultZone: ImportZone;
    /** Productos ya existentes por zona, para actualizar en lugar de duplicar. */
    existingByZone: { [zoneKey: string]: ExistingProduct[] };
    /**
     * Ubicaciones que no existen en el evento: crearlas como categorías
     * dentro de defaultZone (un archivo con "Botellas", "Tragos"… arma la
     * zona completa de una vez) en lugar de rechazar la fila.
     */
    createMissingZones?: boolean;
}

export function matchZone(text: string, zones: ImportZone[]): ImportZone | null {
    const clean = normalizeText(text);
    if (!clean) return null;
    // Una ruta ("Precio Regular / Botellas") solo casa con la categoría de esa ruta.
    const cleanPath = normalizeText(joinZonePath(splitZonePath(text)));
    const byPath = zones.filter(z => z.path && normalizeText(z.path) === cleanPath);
    if (byPath.length > 0) return byPath[0];
    if (splitZonePath(text).length > 1) return null;
    // Un nombre a secas solo sirve si es único: con "Botellas" en dos ramas hay que dar la ruta.
    const exact = zones.filter(z => normalizeText(z.name) === clean);
    if (exact.length === 1) return exact[0];
    if (exact.length > 1) return null;
    const partial = zones.filter(z => {
        const name = normalizeText(z.name);
        return name.indexOf(clean) >= 0 || clean.indexOf(name) >= 0;
    });
    return partial.length === 1 ? partial[0] : null;
}

/** Rutas de las zonas que se llaman igual que `text`, cuando hay más de una (nombre ambiguo). */
export function ambiguousZonePaths(text: string, zones: ImportZone[]): string[] {
    const clean = normalizeText(text);
    if (!clean || splitZonePath(text).length > 1) return [];
    const exact = zones.filter(z => normalizeText(z.name) === clean);
    return exact.length > 1 ? exact.map(z => z.path || z.name) : [];
}

/**
 * Decide, fila por fila, a qué zona va, si se crea o se actualiza un
 * producto que ya existe con el mismo nombre en esa zona, y qué avisos
 * mostrar. Una fila con error se salta; los avisos no la detienen.
 */
export function prepareRows(rows: ImportRow[], options: PrepareOptions): PreparedRow[] {
    const seen: { [key: string]: boolean } = {};

    return rows.map(row => {
        const issues: ImportIssue[] = [];
        const name = String(row.name || '').trim();

        let zone: ImportZone = options.defaultZone;
        let newZone = false;
        if (row.zone) {
            const found = matchZone(row.zone, options.zones);
            const ambiguous = found ? [] : ambiguousZonePaths(row.zone, options.zones);
            if (found) {
                zone = found;
            } else if (ambiguous.length > 0) {
                issues.push({ level: 'error', message: `"${row.zone}" existe en varias ramas: usa la ruta completa (${ambiguous.join(' · ')}).` });
            } else if (options.createMissingZones) {
                // Ruta anidada: cada tramo que falte se crea dentro del anterior.
                const parts = splitZonePath(row.zone);
                const path = joinZonePath(parts);
                zone = { key: NEW_ZONE_PREFIX + normalizeText(path), name: parts[parts.length - 1], path };
                newZone = true;
                issues.push({ level: 'info', message: `"${path}" no existe: se creará como categoría dentro de ${options.defaultZone.name}.` });
            } else {
                issues.push({ level: 'error', message: `La ubicación "${row.zone}" no existe en el evento.` });
            }
        }

        if (!name) issues.push({ level: 'error', message: 'Falta el nombre del producto.' });

        const existing = (options.existingByZone[zone.key] || [])
            .filter(p => normalizeText(p.name) === normalizeText(name));
        let action: ImportAction = existing.length > 0 ? 'update' : 'create';
        const existingKey = existing.length > 0 ? existing[0].key : '';

        // Al actualizar, lo que el archivo no trae se conserva; al crear, se
        // completa con 0 y se avisa.
        const updating = action === 'update';
        const keepPrice = updating && row.price === null;
        const keepInventory = updating && row.inventory === null;

        let price = row.price;
        if (price === null) {
            price = 0;
            if (!keepPrice) issues.push({ level: 'warning', message: 'Sin precio: queda en 0 y oculto hasta que lo corrijas.' });
        } else if (price < 0) {
            issues.push({ level: 'error', message: 'El precio no puede ser negativo.' });
        }

        let inventory = row.inventory;
        if (inventory === null) {
            inventory = 0;
            if (!keepInventory) issues.push({ level: 'warning', message: 'Sin inventario: queda en 0.' });
        } else if (inventory < 0) {
            issues.push({ level: 'error', message: 'El inventario no puede ser negativo.' });
        }

        // Visibilidad: la que diga el archivo; si no dice nada, al crear
        // depende de que se pueda vender y al actualizar se conserva.
        const keepVisible = updating && row.visible === null;
        const sellable = (keepPrice || price > 0) && (keepInventory || inventory > 0);
        let visible = row.visible === null ? sellable : row.visible;
        if (visible && !sellable) {
            visible = false;
            issues.push({ level: 'warning', message: 'Se importa oculto: la app no muestra productos sin precio o sin inventario.' });
        }

        if (updating) {
            const kept: string[] = [];
            if (keepPrice) kept.push('precio');
            if (keepInventory) kept.push('inventario');
            if (keepVisible) kept.push('visibilidad');
            issues.push({
                level: 'info',
                message: `Ya existe en ${zone.name}: se actualiza lo que trae el archivo`
                    + (kept.length ? ` y conserva ${kept.join(', ')}.` : '.'),
            });
        }

        const dedupeKey = zone.key + '|' + normalizeText(name);
        if (name && seen[dedupeKey]) {
            action = 'skip';
            issues.push({ level: 'error', message: 'Repetido en el archivo: se toma la primera aparición.' });
        }
        if (name) seen[dedupeKey] = true;

        if (issues.some(i => i.level === 'error')) action = 'skip';

        return {
            row, action, zoneKey: zone.key, zoneName: zone.name, zonePath: zone.path || '', existingKey, newZone,
            name, price, inventory, visible, keepPrice, keepInventory, keepVisible,
            code: String(row.code || '').trim(),
            supplier: String(row.supplier || '').trim(),
            section: cleanSection(row.section),
            issues,
        };
    });
}

export interface ImportSummary {
    create: number;
    update: number;
    skip: number;
    warnings: number;
    /** Categorías (ubicaciones) que se crearán dentro de la zona actual. */
    newZones: number;
}

export function summarize(prepared: PreparedRow[]): ImportSummary {
    const summary = prepared.reduce((acc, p) => {
        acc[p.action] += 1;
        if (p.action !== 'skip' && p.issues.some(i => i.level === 'warning')) acc.warnings += 1;
        return acc;
    }, { create: 0, update: 0, skip: 0, warnings: 0, newZones: 0 } as ImportSummary);
    summary.newZones = newZoneNames(prepared).length;
    return summary;
}

/** Rutas de las categorías por crear (filas que sí se importan), en orden de aparición. */
export function newZoneNames(prepared: PreparedRow[]): string[] {
    const names: string[] = [];
    prepared.forEach(p => {
        if (p.action === 'skip' || !p.newZone) return;
        const path = p.zonePath || p.zoneName;
        if (!names.some(n => normalizeText(n) === normalizeText(path))) names.push(path);
    });
    return names;
}

// ═══════════════════════════════════════════════════════════════════════════
//  COPIAR PRODUCTOS ENTRE ZONAS
// ═══════════════════════════════════════════════════════════════════════════

/** Lo que vive en una zona: productos propios, referencias y cuántas entradas ocupa. */
export interface ZoneCatalog {
    bricks: ExistingProduct[];
    references: { key: string; brick: string }[];
    /** Productos + referencias vivos: la posición donde entra lo nuevo. */
    count: number;
}

export type CopyExistingPolicy = 'skip' | 'update';

export interface CopyPlanItem {
    /** Producto tal como lo lista la zona origen ($key, name, reference…). */
    product: any;
    zone: ImportZone;
    action: 'create' | 'update' | 'skip';
    /** $key del producto que ya existe en el destino (update/skip). */
    existingKey: string;
    note: string;
}

/**
 * Decide, para cada producto y cada zona destino, si se crea, se actualiza
 * o se omite. Un producto propio "ya existe" si el destino tiene uno con el
 * mismo nombre; una referencia, si el destino ya referencia al mismo producto
 * original. Función pura: los catálogos vienen ya leídos.
 */
export function planCopy(
    products: any[],
    zones: ImportZone[],
    catalogs: { [zoneKey: string]: ZoneCatalog },
    onExisting: CopyExistingPolicy,
): CopyPlanItem[] {
    const plan: CopyPlanItem[] = [];
    zones.forEach(zone => {
        const catalog = catalogs[zone.key] || { bricks: [], references: [], count: 0 };
        products.forEach(product => {
            const isReference = !!(product.reference && product.reference.$key);
            if (isReference) {
                const dup = catalog.references.filter(r => r.brick === product.$key);
                plan.push(dup.length > 0
                    ? { product, zone, action: 'skip', existingKey: dup[0].key, note: `${zone.name} ya referencia este producto.` }
                    : { product, zone, action: 'create', existingKey: '', note: '' });
                return;
            }
            const name = normalizeText(product.name);
            const dup = catalog.bricks.filter(b => normalizeText(b.name) === name);
            if (dup.length === 0) {
                plan.push({ product, zone, action: 'create', existingKey: '', note: '' });
            } else if (onExisting === 'update') {
                plan.push({ product, zone, action: 'update', existingKey: dup[0].key, note: `Ya existe en ${zone.name}: se actualizan precio, inventario y visibilidad.` });
            } else {
                plan.push({ product, zone, action: 'skip', existingKey: dup[0].key, note: `Ya existe en ${zone.name}.` });
            }
        });
    });
    return plan;
}
